import type { ChangeEvent, MouseEvent } from 'react';
import { useRef } from 'react';
import { cn } from '../../../common/functions/cn';
import { combineRefs } from '../../../common/functions/combine-refs';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel, { fieldLabelStyles } from '../../labels/field-label';
import { Input } from '../input-group/input-group';
import { getCurrentTime } from './time-utils';

export type TimeProps = DynamicFieldComponentProps<string | null>;

const inputStyles = cn(
  'appearance-none',
  // Safari doesn't treat a time input as text entry, so clicking one never matches `:focus-visible` and the shared ring never paints.
  'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
  // Safari paints its `12:30 pm` hint on an empty input in the value's own colour, so an empty field reads as filled.
  'data-empty:not-focus-within:text-muted-foreground/50'
);

export const Time = ({
  id,
  name,
  label,
  value,
  error,
  placeholder,
  className,
  inputRef,
  readOnly,
  required,
  disabled,
  onChange,
  onFocus,
  labelStyles,
}: TimeProps) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const combinedRef = combineRefs(inputRef, internalRef);

  const identifier = id ? String(id) : name;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange?.(value || null);
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (!event.currentTarget.value) {
      onChange?.(getCurrentTime());
    }
  };

  if (readOnly) {
    const readOnlyValue = (
      <div id={identifier} className="block py-1 text-sm">
        {value ? value : <EmptyFieldLabel />}
      </div>
    );

    return (
      <div className="flex w-full flex-col">
        {label ? (
          <dl>
            <dt className={cn(fieldLabelStyles, labelStyles)}>{label}</dt>
            <dd className="m-0">{readOnlyValue}</dd>
          </dl>
        ) : (
          readOnlyValue
        )}
        {error && <ErrorLabel>{error}</ErrorLabel>}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      {label && (
        <div className="flex">
          <FieldLabel label={label} name={identifier} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <Input
        ref={combinedRef}
        type="time"
        id={identifier}
        name={name}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={handleChange}
        onClick={handleClick}
        onFocus={onFocus}
        disabled={disabled}
        aria-invalid={!!error}
        data-empty={value ? undefined : true}
        className={cn(inputStyles, className)}
      />
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};
